import { Signal } from "signals";
import Components from "slotworks/components/components";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import Layers from "slotworks/graphics/layers/layers";
import SlotDefinition from "slotworks/model/slot-definition";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import { normalizeIndex, wrapIndex } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contracts/contract";
import easingFactory from "slotworks/utils/easing-factory";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import { RandomRangeInt } from "slotworks/utils/math/random";
import Timer from "slotworks/utils/timer";
import { ReelSpinnerResultBuffer } from "./reel-spinner-result-buffer";
import StandardReelSpinnerResultBuffer from "./standard-reel-spinner-result-buffer";

export default class ReelSpinner {

    public static spinStages: SpinStage[][];

    public static quickSpinStages: SpinStage[][];
    public static skipStages: SpinStage[][];

    public static anticipationStages: SpinStage[][];

    /**
     * Override the layer the anticipation animations are searched for in (SymbolAnimations by default)
     */
    public static anticipationLayer: Layers | Layers[];
    public static minRepeats = 7;
    /**
     * Extends reel to accomodate oversizes symbols overhanging
     */
    public static extended: boolean = false;

    /**
     * Purely to calculate total spin time, nothing else
     */
    public static stopGap: number;

    /**
     * Spin stage to begin reel blur, defaults to "repeat" stage
     */
    public static blurStartStage: number = -1;
    /**
     * Spin stage to end reel blur, defaults to "stop" stage
     */
    public static blurEndStage: number = -1;

    public static bufferType: { new(...args: any[]): ReelSpinnerResultBuffer } = StandardReelSpinnerResultBuffer;

    public static getTotalSpinTime() {
        const spinStages = ReelSpinner.spinStages ? ReelSpinner.spinStages[0] : ReelSpinner.defaultSpinStages;

        const reelSpinTime = spinStages.reduce<number>((total, stage) => {
            if (stage.t) {
                total += stage.t;
            } else {
                const a = ((stage.v * stage.v) - (stage.u * stage.u)) / (2 * stage.s);
                total += Math.abs((stage.v !== stage.u) ? ((stage.v - stage.u) / a) : (stage.s / stage.u));
            }

            return total;
        }, 0);

        return reelSpinTime + ReelSpinner.stopGap * (SlotDefinition.matrixGrid.length - 1);
    }

    /**
     * Adds a minSpin stage to spin stages in order to ensure a minimum spin time
     */
    public static setMinSpinTime(minDuration: number) {
        if (minDuration > 0) {
            const minSpinTime = minDuration - ReelSpinner.getTotalSpinTime();

            const spinStages = ReelSpinner.spinStages ? ReelSpinner.spinStages[0] : ReelSpinner.defaultSpinStages;

            spinStages.find((stage, index) => {
                if (stage.name === "repeat") {
                    spinStages.splice(index, 0, {
                        name: "minSpin",
                        t: minSpinTime,
                        s: Math.round(minSpinTime * stage.u)
                    } as SpinStage);

                    return true;
                }
            });
        }
    }

    private static defaultSpinStages: SpinStage[] = [
        { name: "pullback", s: 0.2, t: 250 } as SpinStage,
        { name: "wait", s: -0, t: 200 } as SpinStage,
        { name: "accelerate", s: -1.2, u: 0, v: -0.01 } as SpinStage,
        { name: "minSpin", s: -1, u: -0.01, v: -0.01 } as SpinStage,
        { name: "repeat", s: -1, u: -0.01, v: -0.01 } as SpinStage,
        { name: "stop", s: -4.2, u: -0.01, v: 0 } as SpinStage,
        { name: "complete", s: 0.2, t: 250 } as SpinStage
    ];

    private static defaultQuickSpinStages: SpinStage[] = [
        { name: "pullback", s: 0.2, t: 250 } as SpinStage,
        { name: "wait", s: -0, t: 200 } as SpinStage,
        { name: "accelerate", s: -1.2, u: 0, v: -0.01 } as SpinStage,
        { name: "minSpin", s: -1, u: -0.01, v: -0.01 } as SpinStage,
        { name: "repeat", s: -1, u: -0.01, v: -0.01 } as SpinStage,
        { name: "stop", s: -4.2, u: -0.01, v: 0 } as SpinStage,
        { name: "complete", s: 0.2, t: 250 } as SpinStage
    ];

    private static defaultSkipStages: SpinStage[] = [
        { name: "stop", s: -4.2, t: 500 } as SpinStage,
        { name: "complete", s: 0.2, t: 250 } as SpinStage
    ];

    private static defaultAnticipationStages: SpinStage[] = [
        { name: "anticipate", s: -8, u: -0.01, v: -0.01 } as SpinStage,
        { name: "stop", s: -12, u: -0.01, v: 0 } as SpinStage
    ];

    public landSignal: Signal = new Signal();
    public onMove: Signal = new Signal();
    public onAnticipationStart: Signal = new Signal();
    public onAnticipationEnd: Signal = new Signal();
    public anticipating: boolean = false;
    public wasAnticipating: boolean = false;

    private reel: ReelComponent;

    private allSymbols: SymbolComponent[];
    private symbolPositions: Rectangle[];

    private topExtendedHiddenSymbol: SymbolComponent;
    private topHiddenSymbol: SymbolComponent;
    private bottomHiddenSymbol: SymbolComponent;

    // The current temporary reel strip, including buffers, results etc
    private reelstrip: string[];
    // The reel strip seen when spinning
    private spinReelstrip: string[];

    private result: ReelstripPosition;
    // The position in the reel strip the last result was spliced in at. Needs to be removed as soon as it is no longer visible
    private tempResultPosition: number = -1;

    private resultBuffer: ReelSpinnerResultBuffer;

    // Configs
    private stages: SpinStage[];

    // State
    private animating: boolean;
    private started: boolean;
    private stage: number = 0;
    private stopTimeout: number;
    private stopping: boolean;
    private waitingToStop: boolean;
    private repeatCount: number = 0;
    private landing: boolean;

    // Motion variables
    private startTime: number;
    private target: SpinStage;
    // Current position is somewhere between where a stage starts and ends, depending on time
    private currentPosition: number = 0;
    // Fixed position is where the reel should be between stages
    private fixedPosition: number = 0;
    // The last distance update
    private lastDistance: number = 0;

    // Contract resolves
    private startedResolve: Function;
    private completeResolve: Function;

    protected anticipationAnimations: AnimatedSprite[];

    private matrixLayer: Layers;
    private animationLayer: Layers;

    constructor(reel: ReelComponent, reelstop: number, reelstrip: string[]) {

        this.reel = reel;

        this.resultBuffer = new ReelSpinner.bufferType();
        this.resultBuffer.init(this.reel);

        this.matrixLayer = this.reel.getMatrixLayer();
        this.animationLayer = this.reel.getAnimationLayer();

        this.reelstrip = [...reelstrip];
        this.spinReelstrip = [...reelstrip];

        this.fixedPosition = this.currentPosition = reelstop;

        this.getSymbolPositions();
        this.addHiddenSymbols();

        if (ReelSpinner.extended) {
            this.enableHiddenSymbols();
        } else {
            this.disableHiddenSymbols();
        }
        this.updateSymbolPositions();

        // Default blur stages
        if (ReelSpinner.blurStartStage === -1) {
            ReelSpinner.blurStartStage = this.getSpinStages().indexOf(this.getSpinStages().find((stage) => stage.name === "repeat"));
        }
        if (ReelSpinner.blurEndStage === -1) {
            ReelSpinner.blurEndStage = this.getSpinStages().indexOf(this.getSpinStages().find((stage) => stage.name === "stop"));
        }
    }

    /**
     * Start the reel spinning using last reel strip (either from result or from constructor)
     */
    public start(delay: number = 0, jumpStart: boolean = false, quickSpin: boolean = false): Contract<void> {
        this.enableHiddenSymbols();
        this.getSymbolPositions();

        this.started = false;
        this.anticipating = false;
        this.wasAnticipating = false;

        Timer.setTimeout(() => {

            Services.sound.event(SoundEvent.reel_N_spin_start, this.reel.index.toString());
            Services.sound.event(SoundEvent.reel_any_spin_start);

            this.startTime = Timer.time;
            this.target = { s: 0, t: 0, u: 0, v: 0, a: 0 } as SpinStage;
            this.lastDistance = 0;
            this.currentPosition = 0;

            if (this.result) {
                this.reelstrip = [...this.spinReelstrip];
                this.fixedPosition = RandomRangeInt(0, this.reelstrip.length);
            }

            this.log(this.reel.index, this.fixedPosition, this.reelstrip);

            this.tempResultPosition = this.fixedPosition;

            // Update reelstrip to include current symbols in view (which may have changed due to an expand etc)
            for (let y = -1; y < this.reel.size + 1; y++) {
                let symbolId;
                if (y < 0) {
                    if (!ReelSpinner.extended) {
                        continue;
                    }
                    symbolId = this.topHiddenSymbol.symbolId;
                } else if (y >= this.reel.size) {
                    if (!ReelSpinner.extended) {
                        continue;
                    }
                    symbolId = this.bottomHiddenSymbol.symbolId;
                } else {
                    symbolId = this.reel.getSymbolAt(y).symbolId;
                }

                let reelStripIndex = this.fixedPosition + y;
                if (reelStripIndex < 0) {
                    reelStripIndex += this.reelstrip.length;
                }

                this.reelstrip[reelStripIndex] = symbolId;
            }

            // Check strip for stacks
            const topSymbol = this.reelstrip[this.fixedPosition];
            const topSymbolDef = SlotDefinition.getSymbolDefinition(topSymbol);
            if (topSymbolDef.size > 1) {
                let stackSize = 1;
                for (let y = 1; y < topSymbolDef.size; y++) {
                    const nextSymbol = this.reelstrip[this.fixedPosition + y];
                    if (nextSymbol === topSymbol) {
                        stackSize++;
                    }
                }

                // Remove unneeded stack continuations above
                let checkIndex = this.fixedPosition;
                for (let y = 0; y < topSymbolDef.size; y++) {
                    checkIndex--;
                    if (checkIndex < 0) {
                        checkIndex += this.reelstrip.length;
                    }

                    // Found stack continuation
                    if (this.reelstrip[checkIndex] === topSymbol || this.reelstrip[checkIndex] + "_expanded" === topSymbol) {
                        if (stackSize < topSymbolDef.size) {
                            stackSize++;
                            continue;
                        }
                        this.log(this.reel.index, "CLEAN UP CONTINUATIONS START", this.reelstrip);
                        this.reelstrip.splice(checkIndex, 1);
                        if (this.fixedPosition > checkIndex) {
                            this.fixedPosition--;
                        }
                        this.log(this.reel.index, "CLEAN UP CONTINUATIONS DONE", this.reelstrip);
                    } else {
                        break;
                    }
                }
            }

            this.setStages(quickSpin ? this.getQuickSpinStages() : this.getSpinStages());

            if (jumpStart) {
                for (let stageIndex = 0; stageIndex < this.stages.length; stageIndex++) {
                    const stage = this.stages[stageIndex];
                    if (stage.name === "repeat") {
                        this.stage = stageIndex;
                    }
                }
            }

            this.animating = true;

            this.nextStage();
        }, delay);

        return new Contract<void>((resolve) => {
            this.startedResolve = () => resolve(null);
        });
    }

    /**
     * Stops the reel on the specified index within the specified reelstrip
     *
     * @param index {number}
     * @param reelstrip {string[]}
     */
    public stop(index: number, reelstrip: string[], delay: number = 0, quickSpin: boolean): Contract<void> {

        if (!this.stopping) {

            this.repeatCount = Math.max(this.repeatCount, Math.ceil(ReelSpinner.minRepeats * 0.5));

            const doStop = () => {
                if (!this.waitingToStop) {
                    this.result = { index, reelstrip };
                    this.waitingToStop = true;
                }
            };

            if (delay > 0) {
                this.stopTimeout = Timer.setTimeout(doStop, delay);
            } else {
                doStop();
            }
        }

        return new Contract<void>((resolve) => {
            this.completeResolve = () => {
                Services.sound.event(SoundEvent.reel_N_spin_end, this.reel.index.toString());
                Services.sound.event(SoundEvent.reel_any_spin_end);
                resolve(null);
            };

            if (!this.animating) {
                resolve(null);
            }
        });
    }

    /**
     * Quickly stops the reel on the specified index within the specified reelstrip
     *
     * @param index {number}
     * @param reelstrip {string[]}
     */
    public skip(index: number, reelstrip: string[]): Contract<void> {
        if (this.animating && !this.stopping) {
            this.repeatCount = ReelSpinner.minRepeats;
            this.waitingToStop = false;
            this.stopping = true;

            Timer.clearTimeout(this.stopTimeout);

            Services.sound.event(SoundEvent.reel_N_skip_start, this.reel.index.toString());
            Services.sound.event(SoundEvent.reel_any_skip_start);

            this.result = { index, reelstrip };

            this.setStages(this.getSkipStages());

            const distanceToWhole = Math.ceil(this.currentPosition - 1) - this.currentPosition;

            this.stages[0] = { ...this.stages[0] };

            const distanceToGo = this.target.s - (this.currentPosition - this.fixedPosition);

            if (this.target.name === "stop" && Math.abs(distanceToGo) < Math.abs(this.stages[0].s)) {
                const difference = Math.floor(this.stages[0].s) - Math.floor(distanceToGo);
                this.stages[0].s -= difference;
            }

            this.stages[0].s += distanceToWhole;

            this.fixedPosition = this.currentPosition;
            this.currentPosition = 0;
            this.startTime = Timer.time;
            this.lastDistance = 0;
            this.target = { s: 0, t: 0, u: 0, v: 0, a: 0 } as SpinStage;

            this.nextStage();
        }

        return new Contract<void>((resolve) => {
            this.completeResolve = () => {
                Services.sound.event(SoundEvent.reel_N_skip_end, this.reel.index.toString());
                Services.sound.event(SoundEvent.reel_any_skip_end);
                resolve(null);
            };

            if (!this.animating) {
                resolve(null);
            }
        });
    }

    /**
     * Slowly stops the reel on the specified index within the specified reelstrip
     *
     * @param index {number}
     * @param reelstrip {string[]}
     */
    public anticipate(index: number, reelstrip: string[]): Contract<void> {
        const anticipationLayers = this.getAnticipationLayers();

        this.onAnticipationStart.dispatch(this.reel.index);
        this.anticipating = true;

        this.anticipationAnimations = [];
        for (const layer of anticipationLayers) {
            const anticipationAnimation = layer.getAnimatedSprite("anticipation_" + this.reel.index);
            if (anticipationAnimation) {
                anticipationAnimation.visible = true;
                anticipationAnimation.loop = true;
                anticipationAnimation.gotoAndPlay();
                this.anticipationAnimations.push(anticipationAnimation);
            }
        }

        Services.sound.event(SoundEvent.reel_N_anticipate_start, this.reel.index.toString());
        Services.sound.event(SoundEvent.reel_any_anticipate_start);

        this.result = { index, reelstrip };

        this.setStages(this.getAnticipationStages());

        const distanceToWhole = Math.ceil(this.currentPosition - 1) - this.currentPosition;

        this.stages[0] = { ...this.stages[0] };
        this.stages[0].s += distanceToWhole;

        this.fixedPosition = this.currentPosition;
        this.currentPosition = 0;
        this.startTime = Timer.time;
        this.lastDistance = 0;
        this.target = { s: 0, t: 0, u: 0, v: 0, a: 0 } as SpinStage;

        this.nextStage();

        return new Contract<void>((resolve) => {
            this.completeResolve = () => {
                this.anticipating = false;
                this.wasAnticipating = true;
                this.onAnticipationEnd.dispatch(this.reel.index);

                Services.sound.event(SoundEvent.reel_N_anticipate_end, this.reel.index.toString());
                Services.sound.event(SoundEvent.reel_any_anticipate_end);

                resolve(null);
            };

            if (!this.animating) {
                resolve(null);
            }
        });
    }

    public update() {

        if (this.animating) {

            const elapsedTime = (Timer.time - this.startTime);

            this.move(elapsedTime, this.target.t);

            if (elapsedTime >= this.target.t) {

                this.nextStage();
            }
        }

    }

    /**
     * If the reels jump, we have to update the result the spinner thinks it's looking at
     */
    public jump(index: number, reelstrip: string[]) {
        this.reset();
        this.result = { index, reelstrip };
    }

    public isStopping() {
        return this.stopping;
    }

    private spinComplete() {

        this.reset();

        this.completeResolve();
    }

    private reset() {
        this.currentPosition = this.fixedPosition;
        this.updateSymbolPositions();

        this.repeatCount = 0;
        this.animating = false;
        this.waitingToStop = false;
        this.stopping = false;

        this.reel.unblur();

        if (this.anticipationAnimations) {
            for (const anticipationAnimation of this.anticipationAnimations) {
                if (anticipationAnimation) {
                    anticipationAnimation.stop();
                    anticipationAnimation.visible = false;
                }
                this.anticipationAnimations = null;
            }
        }

        this.disableHiddenSymbols();
    }

    private move(elapsedTime: number, totalTime: number) {

        elapsedTime = this.applyEasing(elapsedTime, totalTime);

        const distance = (this.target.u * elapsedTime) + (0.5 * this.target.a) * (elapsedTime * elapsedTime);

        this.currentPosition = this.fixedPosition + distance;

        this.onMove.dispatch(distance - this.lastDistance);
        this.updateSymbolVelocities(distance - this.lastDistance);

        this.lastDistance = distance;

        this.updateSymbolPositions();

        this.clearTempReel();
    }

    /**
     * When spinning after a result, the last result appears in the current reelstrip, but should be removed as soon as it's off screen, so it doesn't spin past during the spin
     */
    private clearTempReel() {
        if (this.tempResultPosition > -1) {

            const currentReelPosition = Math.floor(this.currentPosition);
            const minTargetPosition = normalizeIndex(this.tempResultPosition - (this.reel.size + 2), this.reelstrip);
            const maxTargetPosition = normalizeIndex(this.tempResultPosition + (this.reel.size + 2), this.reelstrip);

            let spliceSafe = false;

            if (minTargetPosition < maxTargetPosition) {
                spliceSafe = currentReelPosition < minTargetPosition || currentReelPosition > maxTargetPosition;
            } else {
                spliceSafe = currentReelPosition < minTargetPosition && currentReelPosition > maxTargetPosition;
            }

            if (spliceSafe) {

                if (currentReelPosition > this.tempResultPosition) {
                    const spliceSize = this.reelstrip.length - this.spinReelstrip.length;
                    this.currentPosition -= spliceSize;
                    this.fixedPosition -= spliceSize;
                }
                this.reelstrip = [...this.spinReelstrip];
                this.tempResultPosition = -1;
            }
        }
    }

    private applyEasing(elapsedTime: number, totalTime: number) {
        const timeRatio = elapsedTime / totalTime;

        // Due to frame time, we can end up with timeRatios greater than 1.
        if (this.target.easing && timeRatio <= 1) {
            const easing = easingFactory(this.target.easing);
            elapsedTime = easing(timeRatio) * totalTime;
        }

        return elapsedTime;
    }

    private nextStage() {
        if (this.stage > 0) {
            // Update fixed position
            this.fixedPosition += this.target.s;

            // Fix floating point errors
            const diff = Math.abs(this.fixedPosition - Math.round(this.fixedPosition));
            if (diff > 0 && diff < 0.01) {
                this.fixedPosition = Math.round(this.fixedPosition);
            }

            // Wrap position
            while (this.fixedPosition >= this.reelstrip.length) {
                this.fixedPosition -= this.reelstrip.length;
            }
            while (this.fixedPosition < 0) {
                this.fixedPosition += this.reelstrip.length;
            }
        }

        // Blurred symbols
        if (!this.stopping && this.stage > ReelSpinner.blurStartStage && this.stage < ReelSpinner.blurEndStage) {
            this.reel.blur();
        } else {
            this.reel.unblur();
        }

        if (this.stage === this.stages.length) {
            this.spinComplete();
        } else {

            const currentStage = this.stages[this.stage];
            const previousStage = this.stages[this.stage - 1];

            if (currentStage.name !== "repeat") {
                Services.sound.event(SoundEvent.spinner_stage_complete, currentStage.name);
                Services.sound.event(SoundEvent.reel_N_spinner_stage_complete, this.reel.index.toString(), currentStage.name);
            }

            // Lands
            if (previousStage && previousStage.name === "stop") {
                this.stopping = true;
                this.landing = true;
            }

            this.setTarget(currentStage);

            if (currentStage.name === "repeat") {
                this.repeatCount++;
            }

            if (currentStage.name !== "repeat" || this.waitingToStop) {
                if (currentStage.name === "repeat") {
                    if (!this.stopping && this.repeatCount >= ReelSpinner.minRepeats) {
                        this.stopping = true;
                    } else {
                        this.stage--;
                    }
                }

                this.stage++;
            }

            if (currentStage.name === "repeat" && !this.started) {
                this.started = true;
                this.startedResolve();
            }
        }
    }

    private setTarget(target: SpinStage) {

        target = { ...target };

        if (target.name === "stop") {
            // Add a buffer between the current reel display and the result, to factor in things like stacks and alternating blanks etc
            const bufferSize = this.resultBuffer.getBufferSize(this.result, { index: Math.floor(this.fixedPosition), reelstrip: this.reelstrip });
            if (target.t) {
                target.t *= (Math.abs(target.s) + bufferSize) / Math.abs(target.s);
            }
            target.s -= bufferSize;
        }

        const overlapTime = (Timer.time - this.startTime) - this.target.t;
        const overshootDistance = this.currentPosition - this.target.s;

        // Temporarily snap reels to whole position (no overshoot) for lands
        if (this.landing) {
            this.currentPosition = this.fixedPosition;
            this.updateSymbolPositions();
            Components.matrix.flush();
            this.landSignal.dispatch(this.reel.index);
            this.landing = false;
        }

        if (target.t) {
            target.a = 0;
            target.u = target.v = target.s / target.t;
        } else {
            target.a = ((target.v * target.v) - (target.u * target.u)) / (2 * target.s);
            target.t = Math.abs((target.v !== target.u) ? ((target.v - target.u) / target.a) : (target.s / target.u));
        }

        this.currentPosition = overshootDistance;
        this.target = target;
        this.startTime = Timer.time - overlapTime;

        if (this.stage >= this.stages.length - 1) {
            this.currentPosition = 0;
        }

        if (target.name === "stop") {
            this.spliceInResults();
        }

        this.lastDistance = (this.target.u * overlapTime) + (0.5 * this.target.a) * (overlapTime * overlapTime);
        this.move(this.target.u, overlapTime);
    }

    /**
     * Splices the results into the reel when it's time to stop
     */
    private spliceInResults() {
        // Cancel removal of last result from temp strip, if it hasn't already happened already
        this.tempResultPosition = -1;

        const resultsToSpliceIn: string[] = [];

        const currentIndex = Math.floor(this.fixedPosition);
        let remainingS = this.target.s;
        for (let stageIndex = this.stage + 1; stageIndex < this.stages.length; stageIndex++) {
            remainingS += this.stages[stageIndex].s;
        }
        const targetIndex = Math.ceil(currentIndex + remainingS) - 1;

        const numberOfSymbols = Math.abs(targetIndex - currentIndex);

        if (this.target.s < 0) {

            for (let i = 0; i < numberOfSymbols; i++) {
                resultsToSpliceIn.push(wrapIndex(this.result.index + i - 1, this.result.reelstrip));
            }

            this.log(this.reel.index, "STRIP BEFORE", this.reelstrip, currentIndex);

            this.resultBuffer.generateResultBuffer(resultsToSpliceIn, this.result, { index: currentIndex, reelstrip: this.reelstrip });

            let pushIndex = currentIndex;

            if (pushIndex < 0) {
                pushIndex += this.reelstrip.length;
            } else {
                this.fixedPosition += numberOfSymbols;
            }

            while (resultsToSpliceIn.length) {
                this.reelstrip.splice(pushIndex, 0, resultsToSpliceIn.pop());
            }
            this.log(this.reel.index, "STRIP AFTER", this.reelstrip, currentIndex);

        } else {
            // Backward spinning likely doesn't work well
            const nextPosition = currentIndex + this.reel.size;

            for (let i = 0; i < numberOfSymbols + 1; i++) {
                resultsToSpliceIn.push(wrapIndex(this.result.index - this.reel.size + 1 + i, this.result.reelstrip));
            }

            this.reelstrip = [...this.reelstrip.slice(0, nextPosition), ...resultsToSpliceIn, ...this.reelstrip.slice(nextPosition)];
        }
    }

    private updateSymbolVelocities(distance: number) {
        const velocity = Math.abs(distance);
        for (const symbol of this.allSymbols) {
            symbol.setVelocity(velocity);
        }
    }

    private updateSymbolPositions() {
        let reelIndex = Math.floor(this.currentPosition);
        let offset = 1 - (this.currentPosition - reelIndex);

        if (offset === 1) {
            offset = 0;
            reelIndex--;
        }

        for (let i = 0; i < this.allSymbols.length; i++) {
            const symbol = this.allSymbols[i];
            const position = this.symbolPositions[i].clone();
            const nextPosition = this.symbolPositions[i + 1].clone();

            const lerpedPosition = Rectangle.Lerp(position, nextPosition, offset);

            symbol.setTransform(lerpedPosition);
            symbol.setSymbol(wrapIndex(reelIndex + i - (ReelSpinner.extended ? 1 : 0), this.reelstrip));
        }
    }

    protected addHiddenSymbols() {
        // Create hidden symbol
        this.topHiddenSymbol = this.createSymbolComponent(new Point(this.reel.index, 0), this.matrixLayer, this.animationLayer);

        this.allSymbols = [this.topHiddenSymbol, ...this.reel.getSymbols()];

        if (ReelSpinner.extended) {
            this.topExtendedHiddenSymbol = this.createSymbolComponent(new Point(this.reel.index, -1), this.matrixLayer, this.animationLayer);
            this.bottomHiddenSymbol = this.createSymbolComponent(new Point(this.reel.index, this.reel.size + 1), this.matrixLayer, this.animationLayer);

            this.allSymbols.unshift(this.topExtendedHiddenSymbol);
            this.allSymbols.push(this.bottomHiddenSymbol);
        }
    }

    protected createSymbolComponent(position: Point, matrixLayer: Layers, animationLayer: Layers): SymbolComponent {
        return new SymbolComponent(position, matrixLayer, animationLayer);
    }

    protected disableHiddenSymbols() {
        if (!ReelSpinner.extended) {
            this.topHiddenSymbol.setVisible(false);
        }
    }

    private enableHiddenSymbols() {
        this.topHiddenSymbol.setVisible(true);
        Components.matrix.addHiddenSymbol(this.topHiddenSymbol);
        if (ReelSpinner.extended) {
            this.topExtendedHiddenSymbol.setVisible(true);
            Components.matrix.addHiddenSymbol(this.topExtendedHiddenSymbol);
            this.bottomHiddenSymbol.setVisible(true);
            Components.matrix.addHiddenSymbol(this.bottomHiddenSymbol);
        }
    }

    private getSymbolPositions() {
        const topHiddenSymbolPosition = this.matrixLayer.getPosition(`top_hidden_symbol_${this.reel.index}`);
        const bottomHiddenSymbolPosition = this.matrixLayer.getPosition(`bottom_hidden_symbol_${this.reel.index}`);

        // Get positions of standard symbols

        this.symbolPositions = [];
        this.symbolPositions.push(topHiddenSymbolPosition);

        for (const symbol of this.reel.getSymbols()) {
            const x = symbol.gridPosition.x;
            const y = symbol.gridPosition.y;

            const positionRect = this.matrixLayer.getPosition(`symbol_${x}_${y}`);
            this.symbolPositions.push(positionRect);
        }

        this.symbolPositions.push(bottomHiddenSymbolPosition);

        if (ReelSpinner.extended) {
            const extendedBottomHiddenSymbolPosition = this.matrixLayer.getPosition(`extended_bottom_hidden_symbol_${this.reel.index}`);
            const extendedTopHiddenSymbolPosition = this.matrixLayer.getPosition(`extended_top_hidden_symbol_${this.reel.index}`);

            this.symbolPositions.unshift(extendedTopHiddenSymbolPosition);
            this.symbolPositions.push(extendedBottomHiddenSymbolPosition);
        }
    }

    private getAnticipationLayers() {
        const anticipationLayers = [];
        if (!ReelSpinner.anticipationLayer) {
            anticipationLayers.push(this.animationLayer);
        } else if (ReelSpinner.anticipationLayer instanceof Layers) {
            anticipationLayers.push(ReelSpinner.anticipationLayer);
        } else {
            for (const layer of ReelSpinner.anticipationLayer) {
                anticipationLayers.push(layer);
            }
        }

        return anticipationLayers;
    }

    private setStages(stages: SpinStage[]) {
        this.stages = [...stages];
        if (this.stages[this.stages.length - 1].name === "stop") {
            this.stages.push({ s: 0, t: 0.0001 } as SpinStage);
        }
        this.stage = 0;
    }

    private getSpinStages() {
        return this.getStages(ReelSpinner.defaultSpinStages, ReelSpinner.spinStages);
    }

    private getQuickSpinStages() {
        return this.getStages(ReelSpinner.defaultQuickSpinStages, ReelSpinner.quickSpinStages);
    }

    private getSkipStages() {
        return this.getStages(ReelSpinner.defaultSkipStages, ReelSpinner.skipStages);
    }

    private getAnticipationStages() {
        return this.getStages(ReelSpinner.defaultAnticipationStages, ReelSpinner.anticipationStages);
    }

    private getStages(defaultStages: SpinStage[], stages: SpinStage[][]) {
        let spinStages = defaultStages;
        if (stages && stages.length) {

            spinStages = stages[0];
            if (stages.length > this.reel.index - 1) {
                spinStages = stages[this.reel.index - 1];
            }
        }
        return spinStages;
    }

    private log(...args: any[]) {
        // Logger.info("%c Reel spinner ", "background: #f4e242; color: #333", ...args);
    }

}

export interface SpinStage {
    name: string;
    s: number;
    u: number;
    v: number;
    a: number;
    t: number;
    easing: string;
}

export interface ReelstripPosition {
    index: number;
    reelstrip: string[];
}
